import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Check, Lock } from 'lucide-react';
import { SnapCarousel } from '@/components/SnapCarousel';
import { fetchGlobalSideBets, fetchSideBetPicksForGroup } from '@/lib/supabase-queries';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { GlobalSideBet } from '@/types/side-bets';
import { PicksRevealSheet, type RevealPick } from '@/components/PicksRevealSheet';
import { RevealHighlight } from '@/components/RevealHighlight';

interface SideBetsCarouselProps {
  groupId: string;
}

interface PickInfo {
  selected_option: string;
  other_text: string | null;
}

function useIsDesktop(breakpoint = 1024) {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isDesktop;
}

export function SideBetsCarousel({ groupId }: SideBetsCarouselProps) {
  const { user } = useAuth();
  const [openBets, setOpenBets] = useState<GlobalSideBet[]>([]);
  const [revealBets, setRevealBets] = useState<GlobalSideBet[]>([]);
  const [picks, setPicks] = useState<Record<string, PickInfo>>({});
  const [loaded, setLoaded] = useState(false);
  const [totalMembers, setTotalMembers] = useState(0);
  const [revealBet, setRevealBet] = useState<GlobalSideBet | null>(null);
  const [revealPicks, setRevealPicks] = useState<RevealPick[]>([]);
  const [revealLoading, setRevealLoading] = useState(false);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!user) return;

    const loadBets = async () => {
      try {
        const [bets, userPicksRes, membersRes] = await Promise.all([
          fetchGlobalSideBets(),
          supabase
            .from('global_side_bet_picks')
            .select('side_bet_id, selected_option, other_text')
            .eq('user_id', user.id)
            .eq('group_id', groupId),
          supabase.from('group_members').select('user_id').eq('group_id', groupId),
        ]);

        const pickMap: Record<string, PickInfo> = {};
        (userPicksRes.data || []).forEach((p) => {
          pickMap[p.side_bet_id] = {
            selected_option: p.selected_option,
            other_text: p.other_text,
          };
        });

        setOpenBets(bets.filter((b) => b.status === 'open'));
        setRevealBets(
          bets.filter((b) => b.status === 'locked' || b.status === 'pending_review')
        );
        setPicks(pickMap);
        setTotalMembers(membersRes.data?.length ?? 0);
      } catch {
        const bets = await fetchGlobalSideBets();
        setOpenBets(bets.filter((b) => b.status === 'open'));
        setRevealBets(
          bets.filter((b) => b.status === 'locked' || b.status === 'pending_review')
        );
      }
      setLoaded(true);
    };

    loadBets();
  }, [user, groupId]);

  const handleRevealBet = async (bet: GlobalSideBet) => {
    setRevealBet(bet);
    setRevealLoading(true);
    setRevealPicks([]);
    try {
      const data = await fetchSideBetPicksForGroup(groupId, bet.id);
      const mapped: RevealPick[] = data.map((p) => ({
        user_id: p.user_id,
        display_name: p.display_name,
        avatar_id: p.avatar_id,
        label:
          p.selected_option === 'other'
            ? p.other_text?.trim()
              ? p.other_text.trim()
              : 'אחר'
            : p.selected_option,
      }));
      setRevealPicks(mapped);
    } catch (err) {
      console.warn('fetchSideBetPicksForGroup failed:', err);
    } finally {
      setRevealLoading(false);
    }
  };

  if (!loaded) return null;

  // Mode selection: open takes priority; reveal only when no opens but locked/pending exist
  const mode: 'open' | 'reveal' | null =
    openBets.length > 0 ? 'open' : revealBets.length > 0 ? 'reveal' : null;

  if (mode === null) return null;

  const activeBets = mode === 'open' ? openBets : revealBets;

  const userChoiceLabel = (bet: GlobalSideBet): string | null => {
    const pick = picks[bet.id];
    if (!pick) return null;
    if (pick.selected_option === 'other') return pick.other_text ?? 'אחר';
    return pick.selected_option;
  };

  // ─── OPEN MODE cards (unchanged behavior, links to /side-bets) ───
  const mobileOpenCard = (bet: GlobalSideBet) => {
    const picked = Boolean(picks[bet.id]);
    const choice = userChoiceLabel(bet);

    return (
      <Link
        key={bet.id}
        to={`/group/${groupId}/side-bets`}
        className="block w-[160px] flex-shrink-0"
      >
        <div
          className="bg-card rounded-2xl p-3 h-[120px] flex flex-col overflow-hidden relative"
          style={{
            border: picked
              ? '1px solid rgba(34,197,94,0.45)'
              : '1px solid rgba(196,164,103,0.25)',
          }}
        >
          {picked && (
            <div className="absolute top-2 left-2 w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <Check className="w-3 h-3 text-green-400" />
            </div>
          )}

          <span className="text-[10px] uppercase tracking-widest text-[#C4A467] shrink-0">
            Side Bet
          </span>

          <p className="text-xs font-medium line-clamp-2 mt-1 shrink-0">
            {bet.title_he}
          </p>

          <div className="mt-auto pt-1 overflow-hidden max-h-[24px]">
            {picked && choice ? (
              <div className="text-[10px] text-green-400 truncate">
                הימורך: <span className="font-semibold">{choice}</span>
              </div>
            ) : bet.options && bet.options.length > 0 ? (
              <div className="flex flex-wrap gap-1 max-h-[20px] overflow-hidden">
                {bet.options.slice(0, 2).map((opt, i) => (
                  <span
                    key={i}
                    className="text-[10px] text-muted-foreground bg-muted/30 px-1.5 py-0.5 rounded truncate max-w-[60px]"
                  >
                    {opt}
                  </span>
                ))}
                {bet.options.length > 2 && (
                  <span className="text-[10px] text-muted-foreground">
                    עוד {bet.options.length - 2}...
                  </span>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </Link>
    );
  };

  const desktopOpenCard = (bet: GlobalSideBet) => {
    const pick = picks[bet.id];
    const picked = Boolean(pick);
    const choice = userChoiceLabel(bet);

    return (
      <Link key={bet.id} to={`/group/${groupId}/side-bets`} className="block">
        <div
          className="bg-card rounded-2xl p-3 flex flex-col justify-between"
          style={{
            border: picked
              ? '1px solid rgba(34,197,94,0.45)'
              : '1px solid rgba(196,164,103,0.25)',
          }}
        >
          <span className="text-[10px] uppercase tracking-widest text-[#C4A467]">
            Side Bet
          </span>

          <p className="text-xs font-medium line-clamp-2 flex-1 mt-1">
            {bet.title_he}
          </p>

          {bet.options && bet.options.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {bet.options.map((opt, i) => {
                const isUserChoice =
                  picked &&
                  ((pick!.selected_option !== 'other' && opt === pick!.selected_option) ||
                    (pick!.selected_option === 'other' && opt === 'other'));
                return (
                  <span
                    key={i}
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      isUserChoice
                        ? 'bg-green-500/20 text-green-400 font-semibold'
                        : 'bg-muted/30 text-muted-foreground'
                    }`}
                  >
                    {opt}
                  </span>
                );
              })}
            </div>
          )}

          <div className="mt-2">
            {picked ? (
              <span className="border border-green-500/40 text-green-400 text-[10px] px-2 py-0.5 rounded-full">
                הימורך: {choice} · לחץ לעריכה
              </span>
            ) : (
              <span className="border border-accent text-accent text-[10px] px-2 py-0.5 rounded-full">
                ממתין להימור
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  };

  // ─── REVEAL MODE cards (new — button, opens PicksRevealSheet) ───
  const mobileRevealCard = (bet: GlobalSideBet) => {
    const choice = userChoiceLabel(bet);
    const isPending = bet.status === 'pending_review';

    return (
      <button
        key={bet.id}
        type="button"
        onClick={() => handleRevealBet(bet)}
        className="block w-[160px] flex-shrink-0 text-right"
      >
        <div className="relative bg-card rounded-2xl p-3 pt-8 h-[120px] flex flex-col overflow-hidden border border-violet-400/40 shadow-[inset_0_0_14px_rgba(167,139,250,0.12)]">
          <RevealHighlight variant="compact" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 flex items-center gap-1">
            <Lock className="w-2.5 h-2.5" />
            {isPending ? 'ממתין לאישור' : 'נעול'}
          </span>

          <p className="text-xs font-medium line-clamp-2 mt-1 shrink-0">
            {bet.title_he}
          </p>

          <div className="mt-auto pt-1">
            {choice ? (
              <span className="text-[10px] text-muted-foreground truncate block">
                הימורך: <span className="text-foreground font-semibold">{choice}</span>
              </span>
            ) : (
              <span className="text-[10px] text-muted-foreground/60">לא השתתפת</span>
            )}
          </div>
        </div>
      </button>
    );
  };

  const desktopRevealCard = (bet: GlobalSideBet) => {
    const choice = userChoiceLabel(bet);
    const isPending = bet.status === 'pending_review';

    return (
      <button
        key={bet.id}
        type="button"
        onClick={() => handleRevealBet(bet)}
        className="block w-full text-right"
      >
        <div className="relative bg-card rounded-2xl p-3 pt-12 flex flex-col justify-between overflow-hidden border border-violet-400/40 shadow-[0_0_14px_rgba(167,139,250,0.15),inset_0_0_18px_rgba(167,139,250,0.08)]">
          <RevealHighlight variant="full" />
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {isPending ? 'ממתין לאישור' : 'נעול'}
            </span>
          </div>

          <p className="text-xs font-medium line-clamp-2 flex-1 mt-1">
            {bet.title_he}
          </p>

          {choice && (
            <div className="mt-2 text-[10px] text-muted-foreground">
              הימורך: <span className="text-foreground font-semibold">{choice}</span>
            </div>
          )}
        </div>
      </button>
    );
  };

  const mobileCard = mode === 'open' ? mobileOpenCard : mobileRevealCard;
  const desktopCard = mode === 'open' ? desktopOpenCard : desktopRevealCard;

  // Header content
  const openTotal = openBets.length;
  const openPicked = openBets.filter((b) => picks[b.id]).length;
  const allPicked = mode === 'open' && openTotal > 0 && openPicked === openTotal;

  const headerTitle = mode === 'open' ? 'הימורי צד' : 'הימורי צד נעולים';
  const headerLink =
    mode === 'open'
      ? allPicked
        ? 'ערוך הימורים ←'
        : 'כל הימורי הצד ←'
      : 'כל הימורי הצד ←';

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
          {mode === 'open' ? (
            <Zap size={16} className="text-[#C4A467]" />
          ) : (
            <Lock size={14} className="text-muted-foreground" />
          )}
          {headerTitle}
          {mode === 'open' ? (
            <span
              className={`text-xs font-normal px-2 py-0.5 rounded-full ${
                allPicked
                  ? 'bg-green-500/15 text-green-400'
                  : 'bg-muted/30 text-muted-foreground'
              }`}
            >
              {openPicked}/{openTotal}
            </span>
          ) : (
            <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-muted/30 text-muted-foreground">
              {revealBets.length}
            </span>
          )}
        </h2>
        <Link
          to={`/group/${groupId}/side-bets`}
          className="text-sm text-accent hover:underline"
        >
          {headerLink}
        </Link>
      </div>

      {mode === 'reveal' && (
        <p className="text-xs text-muted-foreground mb-3 -mt-1">
          ראה מה חברי הקבוצה ניחשו
        </p>
      )}

      {isDesktop ? (
        /* Desktop: vertical stacked list — full content */
        <div className="space-y-3">
          {activeBets.map((bet) => desktopCard(bet))}
        </div>
      ) : (
        /* Mobile: horizontal snap carousel — constrained cards */
        <SnapCarousel cardWidth={160} cardWidthDesktop={220} gap={10} peekAmount={20}>
          {activeBets.map((bet) => mobileCard(bet))}
        </SnapCarousel>
      )}

      <PicksRevealSheet
        open={revealBet !== null}
        onClose={() => setRevealBet(null)}
        title={revealBet?.title_he ?? ''}
        subtitle={revealLoading ? 'טוען...' : 'בחירות חברי הקבוצה'}
        picks={revealPicks}
        currentUserId={user?.id}
        totalMembers={totalMembers}
      />
    </div>
  );
}

export default SideBetsCarousel;
